import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Music, Sparkles, X, LogIn } from "lucide-react";
import { useAlun } from "../lib/useAlun";
import { api } from "../lib/api";
import { createAlunRenderer } from "../lib/alunRender";
import { posisiPemain, hitungDekat } from "../lib/alunSim";
import { useMapGestures } from "../lib/useMapGestures";
import { jamWIB, malamDari } from "../lib/sky";
import { useWargaAktif } from "../lib/presence";
import { FONT, GLASS, SAFE_TOP, glassVars, glassBox, namaKota } from "../components/kota/ui";
import { TOKEN_KEY } from "../components/kota/PesanSheet";
import { Sheet } from "../components/kota/Sheet";
import { KTW } from "../components/kerja/KTW";
import { AlunTopBar } from "../components/alun/AlunTopBar";
import { ChatBar } from "../components/alun/ChatBar";
import { KartuPemain } from "../components/alun/KartuPemain";
import { EmoteRadial } from "../components/alun/EmoteRadial";
import { AirMancurSheet } from "../components/alun/AirMancurSheet";
import { AvatarEditor } from "../components/alun/AvatarEditor";
import { KameraDock } from "../components/alun/KameraDock";
import { OnlineSheet } from "../components/alun/OnlineSheet";
import { RadioPlayer } from "../components/kota/RadioPlayer";
import { useRadioCtx } from "../lib/RadioContext";

const HINT_KEY = "jawsel_alun_hint";

// Alun-alun kota — ruang sosial real-time: ketuk tanah untuk berjalan, ketuk karaktermu untuk emote, ketuk warga lain untuk menyapa,
// ketuk bangku untuk duduk, ketuk air mancur untuk melempar koin harapan. Tanpa KTW = menonton saja.
export default function Alun() {
  const { id } = useParams();
  useWargaAktif("alun");
  const nav = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const alun = useAlun(id, token);
  // Radio Jawsel tetap mengudara di Alun-alun (pemutar global) — tampil sebagai ikon mini kiri-atas, ketuk untuk membuka
  const radio = useRadioCtx();
  const [radioMini, setRadioMini] = useState(true);
  // Misi harian "Mampir ke Alun-alun": dicatat sekali saat berhasil masuk sebagai pemain
  useEffect(() => { if (token && alun.status === "masuk") api.misiAksi(token, "alun").then(r => (r.baru || []).forEach(j => toast.success(`Misi harian selesai: ${j}`))).catch(() => {}); }, [token, alun.status]);
  const canvasRef = useRef(null), rendRef = useRef(null);
  const [pilih, setPilih] = useState(null);     // id pemain yang diketuk
  const [radial, setRadial] = useState(null);   // [sx, sy] posisi layar karakter sendiri saat menu emote terbuka
  const [sheet, setSheet] = useState(null);     // air | karakter | ktw | online | null
  const [dekat, setDekat] = useState(0);
  const [dekatAir, setDekatAir] = useState(false);
  const [hint, setHint] = useState(() => !localStorage.getItem(HINT_KEY));
  const [malam, setMalam] = useState(() => malamDari(jamWIB()));   // malam otomatis ikut jam WIB
  const malamRef = useRef(malam), pilihRef = useRef(null);
  useEffect(() => { malamRef.current = malam; }, [malam]);
  useEffect(() => { pilihRef.current = pilih; }, [pilih]);
  const S = alun.ref.current;
  const me = S.meId ? S.players.get(S.meId) : null;
  const dudukSaya = !!me?.duduk;                // langsung dari keadaan pemain (re-render lewat alun.tick saat server mengirim duduk/berdiri)
  const penonton = alun.status === "penonton" || alun.status === "tolak";

  // Renderer + loop render
  useEffect(() => {
    const r = createAlunRenderer(canvasRef.current);
    rendRef.current = r;
    alun.fxRef.current = f => r.tambahFx(f);
    let raf;
    const loop = () => { r.render(S.players, S.meId, alun.now(), { malam: malamRef.current, pilih: pilihRef.current }); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    // Kait baca-saja untuk pengujian otomatis (posisi layar badan pemain, daftar pemain, posisi layar bangku/air mancur, sudut kamera)
    window.__alun = {
      pemain: () => [...S.players.values()].map(p => ({ id: p.id, username: p.username, duduk: p.duduk })),
      layar: id => { const p = S.players.get(id); if (!p || !S.layout) return null; const pos = posisiPemain(p, alun.now(), S.layout.kecepatan); return r.proj(pos.x, pos.y, p.duduk ? 30 : 22); },
      bangku: i => { const b = S.layout?.bangku[i]; return b ? r.proj(b.slot[0][0], b.slot[0][1], 10) : null; },
      airMancur: () => { const a = S.layout?.air_mancur; return a ? r.proj(a.x, a.y, 8) : null; },
      kamera: () => ({ ...r.cam }),
    };
    return () => { cancelAnimationFrame(raf); r.destroy(); delete window.__alun; };
  }, [id]); // eslint-disable-line
  useEffect(() => { if (alun.layout) rendRef.current?.setLayout(alun.layout); }, [alun.layout]);

  // Statistik berkala: orang di dekatmu, dekat air mancur, jam malam
  useEffect(() => {
    const t = setInterval(() => {
      const L = S.layout; if (!L) return;
      const now = alun.now();
      setDekat(hitungDekat(S.players, S.meId, now, L));
      const p = S.meId ? S.players.get(S.meId) : null;
      if (p) { const pos = posisiPemain(p, now, L.kecepatan), a = L.air_mancur; setDekatAir(Math.hypot(pos.x - a.x, pos.y - a.y) <= a.r + 130); }
      setMalam(malamDari(jamWIB()));
    }, 500);
    return () => clearInterval(t);
  }, [alun, S]);
  useEffect(() => { if (hint) { const t = setTimeout(() => { setHint(false); localStorage.setItem(HINT_KEY, "1"); }, 10000); return () => clearTimeout(t); } }, [hint]);

  const jalanKe = useCallback((x, y) => {
    alun.aksi.jalan(x, y); rendRef.current.ikuti(true);
    rendRef.current.tambahFx({ jenis: "tujuan", x, y, at: alun.now(), ms: 700 });
  }, [alun]);

  const onTap = useCallback((lx, ly) => {
    const r = rendRef.current; if (!r || !S.layout) return;
    const h = r.hit(lx, ly, S.players, alun.now());
    if (hint) { setHint(false); localStorage.setItem(HINT_KEY, "1"); }
    if (h.jenis === "pemain") {
      if (h.id === S.meId) { const pos = posisiPemain(S.players.get(S.meId), alun.now(), S.layout.kecepatan); setRadial(r.proj(pos.x, pos.y, 0)); setPilih(null); }
      else setPilih(h.id);
      return;
    }
    setPilih(null);
    if (penonton) { if (h.jenis === "air_mancur") setSheet("air"); return; }
    if (h.jenis === "bangku") { alun.aksi.duduk(h.b, h.slot); r.ikuti(true); return; }
    if (h.jenis === "air_mancur") {
      const a = S.layout.air_mancur, pos = posisiPemain(S.players.get(S.meId), alun.now(), S.layout.kecepatan);
      const d = Math.hypot(pos.x - a.x, pos.y - a.y) || 1, k = (a.r + 44) / d;
      if (d > a.r + 110) jalanKe(a.x + (pos.x - a.x) * k, a.y + (pos.y - a.y) * k);
      setSheet("air"); return;
    }
    jalanKe(h.x, h.y);
  }, [S, alun, penonton, hint, jalanKe]);
  const gestures = useMapGestures(rendRef, onTap, { ketukDua: false });   // tanpa zoom ketuk 2× (permintaan user) → ketuk jalan langsung

  const dipilih = pilih ? S.players.get(pilih) : null;
  useEffect(() => { if (pilih && !S.players.has(pilih)) setPilih(null); }, [alun.tick, pilih, S]);
  function hampiri(p) {
    const now = alun.now(), a = posisiPemain(p, now, S.layout.kecepatan), m = posisiPemain(S.players.get(S.meId), now, S.layout.kecepatan);
    const d = Math.hypot(m.x - a.x, m.y - a.y) || 1; jalanKe(a.x + (m.x - a.x) / d * 34, a.y + (m.y - a.y) / d * 34);
  }
  const keProfil = () => nav(`/profil?kota=${id}`);
  // Pilih dari daftar online: buka kartu & lompatkan kamera ke posisinya (pemain bisa di luar layar)
  const lihatPemain = p => { setSheet(null); setPilih(p.id); const pos = posisiPemain(p, alun.now(), S.layout.kecepatan); rendRef.current?.lihat(pos.x, pos.y); };

  return (
    <div data-testid="alun-page" onPointerDownCapture={radio.onFirstTouch} style={{ position: "fixed", inset: 0, overflow: "hidden", background: "#9ccc73", color: "#141414", fontFamily: FONT, ...glassVars(malam > 0.5) }}>
      <canvas ref={canvasRef} data-testid="alun-canvas" {...gestures} style={{ display: "block", width: "100%", height: "100%", touchAction: "none", cursor: "pointer" }} />

      <AlunTopBar me={me} saldo={alun.saldo} online={alun.online} status={alun.status} onBack={() => nav(`/kota/${id}`)} onKarakter={() => setSheet("karakter")} onOnline={() => setSheet("online")} />
      <KameraDock onPutar={d => rendRef.current?.putar(d)} onPusat={() => rendRef.current?.ikuti(true)} bisaPusat={!!me} />
      <RadioPlayer radio={radio} mini={radioMini} onMini={setRadioMini} tampilMati top={`calc(${SAFE_TOP} + 66px)`} right={12} />

      {alun.ajakan && (
        <div data-testid="ajakan-joget" style={{ position: "absolute", left: 12, right: 12, top: `calc(${SAFE_TOP} + 118px)`, display: "flex", justifyContent: "center", zIndex: 8 }}>
          <div style={{ ...glassBox, background: GLASS.bgStrong, borderRadius: 20, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, maxWidth: 420, width: "100%" }}>
            <span style={{ width: 36, height: 36, borderRadius: 99, background: GLASS.accentBg, display: "grid", placeItems: "center", flex: "none" }}><Music size={16} color={GLASS.accent} /></span>
            <span style={{ flex: 1, fontSize: 13, lineHeight: 1.3 }}><b>{alun.ajakan.nama}</b> mengajakmu joget bareng</span>
            <button data-testid="joget-terima" onClick={() => alun.aksi.jawabJoget(alun.ajakan.dari, true)} style={{ padding: "8px 14px", borderRadius: 99, border: 0, background: GLASS.accent, color: "#fff", fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: FONT }}>Ayo!</button>
            <button data-testid="joget-tolak" aria-label="Tolak" onClick={() => alun.aksi.jawabJoget(alun.ajakan.dari, false)} style={{ width: 32, height: 32, borderRadius: 99, border: 0, background: "var(--g-inner)", color: GLASS.ink, display: "grid", placeItems: "center", cursor: "pointer" }}><X size={14} /></button>
          </div>
        </div>
      )}

      {hint && !penonton && alun.status === "masuk" && (
        <div data-testid="alun-hint" style={{ position: "absolute", left: 12, right: 12, top: `calc(${SAFE_TOP} + 118px)`, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
          <span style={{ ...glassBox, borderRadius: 99, padding: "8px 14px", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 7, textAlign: "center" }}><Sparkles size={13} color="#d9a21b" /> Ketuk tanah untuk jalan · ketuk karaktermu untuk emote · ketuk warga lain untuk menyapa</span>
        </div>
      )}

      {alun.status === "tolak" && (
        <div data-testid="alun-tolak" style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", zIndex: 9, pointerEvents: "none" }}>
          <div style={{ ...glassBox, background: GLASS.bgStrong, borderRadius: 24, padding: 18, maxWidth: 320, textAlign: "center", pointerEvents: "auto" }}>
            <p style={{ margin: "0 0 12px", fontSize: 13.5, lineHeight: 1.5 }}>{alun.tolak}</p>
            <button data-testid="tolak-masuk" onClick={keProfil} style={{ width: "100%", padding: "12px", borderRadius: 99, border: 0, background: GLASS.accent, color: "#fff", fontWeight: 800, cursor: "pointer", fontFamily: FONT, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}><LogIn size={15} /> Masuk dengan PIN</button>
            <button data-testid="tolak-tonton" onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} style={{ width: "100%", marginTop: 6, padding: "10px", borderRadius: 99, border: 0, background: "transparent", color: GLASS.ink, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Tonton saja</button>
          </div>
        </div>
      )}

      {dipilih && dipilih.id !== S.meId && !sheet && (
        <KartuPemain p={dipilih} onClose={() => setPilih(null)}
          onHampiri={() => { if (penonton) return toast("Masuk dengan KTW untuk ikut bermain"); hampiri(dipilih); }}
          onLambai={() => { if (penonton) return toast("Masuk dengan KTW untuk ikut bermain"); alun.aksi.emote("lambai", dipilih.id); }}
          onJoget={() => { if (penonton) return toast("Masuk dengan KTW untuk ikut bermain"); alun.aksi.ajakJoget(dipilih.id); }}
          onKTW={() => setSheet("ktw")} />
      )}
      {radial && me && <EmoteRadial at={radial} onClose={() => setRadial(null)} onPilih={k => { alun.aksi.emote(k); setRadial(null); }} />}

      <ChatBar dekat={dekat} log={alun.log} duduk={dudukSaya} penonton={penonton} onKirim={t => alun.aksi.chat(t)} onBerdiri={() => alun.aksi.berdiri()} onMasuk={keProfil} />

      {sheet === "air" && <AirMancurSheet harapan={alun.harapan} saldo={alun.saldo} dekat={dekatAir} penonton={penonton} onLempar={t => { alun.aksi.lemparKoin(t); setSheet(null); }} onClose={() => setSheet(null)} />}
      {sheet === "online" && <OnlineSheet players={S.players} meId={S.meId} onPilih={lihatPemain} onClose={() => setSheet(null)} />}
      {sheet === "karakter" && me && (
        <Sheet testid="karakter-sheet" title="Karakterku" sub="Ubah tampilan karaktermu di Alun-alun" onClose={() => setSheet(null)}>
          <AvatarEditor token={token} onSimpan={av => { if (me) me.avatar = av; setSheet(null); }} />
        </Sheet>
      )}
      {sheet === "ktw" && dipilih && (
        <Sheet testid="ktw-pemain-sheet" title="Kartu Warga Jawsel" sub={`@${dipilih.username} · Kota ${namaKota(dipilih.kota)}`} onClose={() => setSheet(null)} tall={false}>
          <KTW w={{ id: dipilih.id, nama: dipilih.nama, ig_username: dipilih.username, city_id: dipilih.kota, created_at: dipilih.created_at }} lv={dipilih.lv} />
        </Sheet>
      )}
      <Toaster theme="light" position="top-center" />
    </div>
  );
}
